import {analyticsLogger} from './analyticsLogger';

/**
 * Where a Knowledge navigation came from, carried in router state as `source`.
 * Entry points outside Knowledge set their own (`left-nav`, `left-nav-knowledge`,
 * `home-chip`, `search`); a view with no source was opened by URL, refresh or
 * an external link and logs `route`.
 */
export const KNOWLEDGE_SOURCES = {
  home: 'knowledge-home',
  homeSearch: 'knowledge-home-search',
  card: 'knowledge-card',
  topic: 'knowledge-topic',
  quickLink: 'knowledge-quick-link',
  breadcrumb: 'knowledge-breadcrumb',
} as const;

export const knowledgeLinkState = (source: string) => ({source});

export const readKnowledgeSource = (state: unknown): string => {
  if (state && typeof state === 'object') {
    const value = (state as Record<string, unknown>).source;
    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }
  return 'route';
};

export type KnowledgeMenuSelection =
  | 'knowledge-home'
  | 'category'
  | 'topic'
  | 'subtopic'
  | 'ai-applied-hub'
  | 'none';

/** Logged when the left-nav flyout closes; `none` means it was opened and left. */
export const logKnowledgeMenuClosed = ({
  openMs,
  selection,
}: {
  openMs: number;
  selection: KnowledgeMenuSelection;
}) => analyticsLogger().logEvent('KNOWLEDGE_MENU_CLOSED', {open_ms: openMs, selection});

export type KnowledgeHomeItemType =
  | 'category'
  | 'topic'
  | 'more_topics'
  | 'article'
  | 'request'
  | 'ai-applied-hub'
  | 'guide';

export const logKnowledgeHomeItemClicked = ({
  itemType,
  categorySlug,
  mode,
  queryLength,
  rank,
  groupRank,
}: {
  itemType: KnowledgeHomeItemType;
  categorySlug?: string;
  mode: 'browse' | 'search';
  queryLength?: number;
  /** 1-based position within its list: a card's topics, or a search section. */
  rank?: number;
  /** 1-based position of the category group an article search result sat in. */
  groupRank?: number;
}) =>
  analyticsLogger().logEvent('KNOWLEDGE_HOME_ITEM_CLICKED', {
    item_type: itemType,
    category_slug: categorySlug,
    mode,
    query_length: queryLength,
    rank,
    group_rank: groupRank,
  });

/** Query text is not logged, matching global search, which logs only its length. */
export const logKnowledgeHomeSearched = ({
  queryLength,
  topicResults,
  articleResults,
  categoriesWithResults,
}: {
  queryLength: number;
  topicResults: number;
  articleResults: number;
  categoriesWithResults: number;
}) =>
  analyticsLogger().logEvent('KNOWLEDGE_HOME_SEARCHED', {
    query_length: queryLength,
    topic_results: topicResults,
    article_results: articleResults,
    categories_with_results: categoriesWithResults,
  });
